import { useId, useState, type InputHTMLAttributes } from "react";
import { cn } from "@/lib/utils";

interface FormFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "onChange"> {
  label: string;
  value?: string;
  error?: string;
  onChange?: (value: string) => void;
  highlight?: boolean;
  /** Hint visuel pour la validation EN PRÉPARATION (cf. lib/bienPrepValidation.ts). */
  missingHint?: "required" | "mailing";
  /**
   * Rend le libellé SOUS le contrôle, en petit et centré, pour les champs
   * réunis dans un FieldGroup (« Pièces » : chambres · s. de bain · douches).
   * Le libellé du groupe porte alors le sens, celui-ci ne fait que désigner
   * la case — d'où sa taille réduite.
   */
  labelBelow?: boolean;
  /**
   * Alignement de la valeur dans le champ. "center" pour les valeurs courtes
   * et calibrées (code postal, étage) : centrées, elles se lisent comme une
   * donnée et non comme un début de saisie libre.
   */
  align?: "left" | "center";
}

export function FormField({
  label,
  value = "",
  error,
  onChange,
  className,
  readOnly,
  // Prop héritée du système c_level — ne pilote plus la couleur du label
  // depuis le commit 064ca14, mais conservée dans l'interface pour ne pas
  // casser tous les callers existants (cf. BienDetailPage). Renommé `_highlight`
  // pour signaler à ESLint qu'on l'ignore volontairement.
  highlight: _highlight,
  missingHint,
  labelBelow = false,
  align = "left",
  ...props
}: FormFieldProps) {
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const isFilled = value.length > 0;
  const generatedId = useId();
  const inputId = props.id ?? generatedId;

  // Si missingHint est actif ET le champ est encore vide → bordure colorée
  // pour signaler le manque. Si rempli, on revient au comportement normal.
  const showMissingBorder = !readOnly && !isFilled && !isFocused && !error && !!missingHint;
  const missingBorderClass =
    missingHint === "required"
      ? "border border-solid border-danger"
      : missingHint === "mailing"
        ? "border border-solid border-warning"
        : "";

  const borderClasses = readOnly
    ? "border border-solid border-transparent cursor-default"
    : error
      ? "border border-solid border-danger"
      : showMissingBorder
        ? missingBorderClass
        : isFocused
          ? "border border-solid border-border-focus"
          : isFilled && isHovered
            ? "border border-dashed border-border-hover"
            : isFilled
              ? "border border-solid border-transparent"
              : isHovered
                ? "border border-dashed border-border-hover"
                : "border border-dashed border-border";

  // Couleur du label : on ne colore QUE si le champ est vide ET tagué par
  // le système de validation EN PRÉPARATION/DISPONIBLE (cf. lib/bienPrepValidation.ts).
  // Le prop `highlight` (c_level pré-existant) n'affecte plus la couleur du
  // label depuis qu'on a retiré sa légende — il créait une incohérence avec
  // les bordures (label rouge sans bordure rouge sur les champs simplement
  // "ELE-relevant" mais pas validés). Priorité : required > mailing > muted.
  const labelColorClass = isFilled
    ? "text-text-muted"
    : missingHint === "required"
      ? "text-danger"
      : missingHint === "mailing"
        ? "text-warning"
        : "text-text-muted";

  // Quand maxLength est défini, on affiche un compteur "x/max" à droite du
  // label pour informer l'utilisateur (saisie déjà bloquée nativement par
  // l'attribut maxLength).
  const showCounter = typeof props.maxLength === "number";

  const labelBlock = (
    <div
      className={cn(
        "flex items-baseline justify-between",
        labelBelow ? "justify-center px-0.5 mt-1" : "px-3 mb-1",
      )}
    >
      <label
        htmlFor={inputId}
        className={cn(
          "font-medium tracking-wider uppercase truncate",
          labelBelow ? "text-[9.5px]" : "text-xs",
          labelColorClass,
        )}
      >
        {label}
      </label>
        {showCounter && (
          <span className={cn(
            "text-[10px] font-medium",
            value.length >= (props.maxLength ?? 0) ? "text-danger" : "text-text-muted",
          )}>
          {value.length}/{props.maxLength}
        </span>
      )}
    </div>
  );

  const input = (
    <input
        className={cn(
          "px-3 py-2 bg-surface-2 rounded-md text-sm",
          align === "center" && "text-center",
          isFilled ? "text-text-primary" : "text-text-muted",
          "placeholder:text-text-muted outline-none shadow-none",
          "transition-colors duration-150",
          borderClasses
        )}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        onFocus={() => !readOnly && setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onMouseEnter={() => !readOnly && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        placeholder={readOnly ? "—" : "..."}
        readOnly={readOnly}
      tabIndex={readOnly ? -1 : undefined}
      {...props}
      id={inputId}
    />
  );

  return (
    <div className={cn("flex flex-col", className)}>
      {!labelBelow && labelBlock}
      {input}
      {labelBelow && labelBlock}
      {error && (
        <span className="text-xs text-danger mt-1 px-3">{error}</span>
      )}
    </div>
  );
}
